import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngxs/store';
import { CompanyInvitation } from 'on-shared/_models/CompanyInvitation';
import { OnUser } from 'on-shared/_models/OnUser';
import { UserPrefill } from 'on-shared/_models/UserPrefill';
import { CompanyAction } from 'projects/onexpense/src/app/shared/stores/company/company.action';
import { InvitationPrefillFormComponent } from './invitation-prefill-form/invitation-prefill-form.component';

@Component({
    selector: 'on-add-employee',
    templateUrl: './add-employee.component.html',
    styleUrls: ['./add-employee.component.scss'],
})
export class AddEmployeeComponent implements OnInit {
    /**
     * The employees of the company, used to pick a manager in the profile.
     */
    @Input() allEmployees: OnUser[] = [];

    /**
     * Set when correcting an invitation that is still pending: the address is
     * fixed and only the prepared profile is updated, without renewing the
     * link nor sending a new mail.
     */
    @Input() invitationToEdit: CompanyInvitation;

    employeesText: string;
    singleEmail: string;
    incorrectEmails: string[];

    mode: 'bulk' | 'single' = 'bulk';
    showPrefill = false;

    isLoading = false;

    @ViewChild(InvitationPrefillFormComponent)
    prefillFormComponent: InvitationPrefillFormComponent;

    constructor(
        private activeModal: NgbActiveModal,
        private store: Store,
    ) {}

    ngOnInit() {
        if (this.invitationToEdit) {
            this.mode = 'single';
            this.showPrefill = true;
            this.singleEmail = this.invitationToEdit.InvitedEmail;
        }
    }

    get isEditing(): boolean {
        return !!this.invitationToEdit;
    }

    get prefillToEdit(): UserPrefill {
        return this.invitationToEdit ? this.invitationToEdit.Prefill : null;
    }

    switchMode(mode: 'bulk' | 'single') {
        if (this.isEditing) {
            return;
        }

        this.mode = mode;
        this.incorrectEmails = [];
    }

    save() {
        const emails = this.readEmails();

        if (emails.length === 0) {
            this.activeModal.dismiss();
            return;
        }

        this.incorrectEmails = emails.filter(
            (email) => !this.validateEmail(email),
        );

        if (this.incorrectEmails.length > 0) {
            return;
        }

        if (this.prefillFormComponent && !this.prefillFormComponent.isValid()) {
            return;
        }

        const prefill = this.prefillFormComponent
            ? this.prefillFormComponent.buildPrefill()
            : null;

        const invitations = emails.map((email) => {
            const invitation = new CompanyInvitation();
            invitation.InvitedEmail = email;
            invitation.Prefill = prefill;
            return invitation;
        });

        // Editing only updates the prepared profile, inviting sends the mails
        const action = this.isEditing
            ? new CompanyAction.UpdateInvitation(invitations[0])
            : new CompanyAction.InviteEmployees(invitations);

        this.isLoading = true;
        this.store.dispatch(action).subscribe({
            next: () => {
                this.isLoading = false;
                this.activeModal.close();
            },
            // The profile is kept on screen so it can be corrected and sent
            // again, the error toast tells what went wrong.
            error: () => {
                this.isLoading = false;
            },
        });
    }

    private readEmails(): string[] {
        if (this.mode === 'single') {
            const email = (this.singleEmail || '').trim();
            return email.length > 0 ? [email] : [];
        }

        // Do not take empty lines
        return (this.employeesText || '')
            .split('\n')
            .map((email) => email.trim())
            .filter((email) => email.length > 0);
    }

    private validateEmail(email: string): boolean {
        // tslint:disable-next-line:max-line-length
        const re =
            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(email);
    }
}
